import React, {ComponentProps, FC} from 'react';

import {Colors, Text, Title} from '@optimaxdev/design-system';
import {Icon} from '@optimaxdev/icons';

import classNames from 'classnames';

import {Price} from 'components/price/price';
import {VISIBLY_PRICE} from 'constants/success';

import {DESKTOP_INSTRUMENTS, MOBILE_INSTRUMENTS} from './constants';
import * as VisiblyLogo from './img/visibly-logo.svg';

import s from './eyeExamPopupContent.scss';

type PropsType = {
    isVista?: boolean;
};

const commonTextProps: ComponentProps<typeof Text> = {
    color: Colors.limedSpruce,
    lineHeight: '24px',
    size: '16px',
};

const commonInfoTextProps: ComponentProps<typeof Text> = {
    color: Colors.limedSpruce,
    lineHeight: '21px',
    size: '14px',
};

/**
 * component with eye exam info on TYP
 */
export const EyeExamPopupContent: FC<PropsType> = ({isVista}) => {
    const isDesktop = __BUILD_TYPE__ === 'desktop';

    const instruments = isDesktop ? DESKTOP_INSTRUMENTS : MOBILE_INSTRUMENTS;

    const titleColor = isDesktop ? Colors.limedSpruce : Colors.mineShaft;

    return (
        <>
            <div className={s.titleWrapper}>
                <Title
                    className={s.title}
                    color={titleColor}
                    fontSize="16px"
                    fontWeight="medium"
                    letterSpacing="normal"
                    level={4}
                    margin="0 0 12px 0"
                >
                    Renew your prescription online with
                    <Icon icon={VisiblyLogo} width={64} />
                </Title>
                <Text {...commonTextProps}>
                    {isVista ? (
                        <>
                            Skip the doctor&apos;s office and take an online vision test (already
                            included in contacts&apos; price). Plus, get your prescription renewed!
                        </>
                    ) : (
                        <>
                            Skip the doctor&apos;s office and take an online vision test for only{' '}
                            <Price>{VISIBLY_PRICE}</Price>.
                        </>
                    )}
                </Text>{' '}
                <Text {...commonTextProps}>
                    Click &apos;Start Vision Test&apos; to take it now.
                </Text>
            </div>
            <div className={classNames(s.infoBlock, {[s.desktop]: isDesktop})}>
                <Title
                    color={titleColor}
                    fontSize="14px"
                    fontWeight={isDesktop ? 'medium' : 'bold'}
                    level={5}
                    lineHeight="18px"
                    margin={isDesktop ? '0 0 10px 0' : '0 0 6px 0'}
                >
                    To start the vision test, you&apos;ll need:
                </Title>
                <ul className={classNames(s.instrumentsList, {[s.desktop]: isDesktop})}>
                    {instruments.map((item, index) => (
                        <React.Fragment key={item.title}>
                            <li className={classNames(s.instrumentItem, {[s.desktop]: isDesktop})}>
                                <div className={classNames(s.imgWrapper, {[s.desktop]: isDesktop})}>
                                    <Icon
                                        color={Colors.codGray}
                                        icon={item.icon}
                                        strokeWidth={item.strokeWidth}
                                        width={item.iconWidth}
                                    />
                                </div>
                                <Text
                                    as="p"
                                    color={isDesktop ? Colors.limedSpruce : Colors.blueCharcoal}
                                    lineHeight={isDesktop ? '18px' : '14px'}
                                    size={isDesktop ? '13px' : '12px'}
                                    textAlign="center"
                                >
                                    {item.text}
                                </Text>
                            </li>
                            {index !== instruments.length - 1 && <div className={s.verticalLine} />}
                        </React.Fragment>
                    ))}
                </ul>
            </div>
            <div className={classNames(s.infoBlock, s.secondBox)}>
                <Title
                    color={titleColor}
                    fontSize="14px"
                    fontWeight="bold"
                    level={5}
                    lineHeight="18px"
                    margin="0 0 6px 0"
                >
                    The vision test is eligible for those who:
                </Title>
                <ul className={s.conditionsList}>
                    {isVista && (
                        <li>
                            <Text {...commonInfoTextProps}>
                                Have a single-vision distance prescription
                            </Text>
                        </li>
                    )}
                    <li>
                        <Text {...commonInfoTextProps}>Are between 18 and 55 years old</Text>
                    </li>
                    <li>
                        <Text {...commonInfoTextProps}>
                            Reside in an eligible state. Due to some states&apos; regulation, these
                            states are ineligible for the online vision test: AK, DE, ID, KS, LA,
                            ME, MI, NJ, NM, OK, RI, SC, VT, WV
                        </Text>
                    </li>
                </ul>
            </div>
        </>
    );
};
